import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Stack,
  Tooltip,
  Snackbar,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem as SelectMenuItem,
  TablePagination,
  Skeleton,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { pageApi } from '../../services/api';
import type { Page } from '../../models';

interface PaginationState {
  page: number;
  pageSize: number;
  totalCount: number;
}

interface PageListParams {
  page: number;
  page_size: number;
  search?: string;
  ordering?: string;
  status?: string;
  is_draft?: boolean;
  draft_of?: boolean;
}

const LoadingTableRow = () => (
  <>
    {[...Array(5)].map((_, index) => (
      <TableRow key={index}>
        <TableCell>
          <Skeleton animation="wave" />
        </TableCell>
        <TableCell>
          <Skeleton animation="wave" width={120} />
        </TableCell>
        <TableCell>
          <Skeleton animation="wave" width={100} />
        </TableCell>
        <TableCell>
          <Skeleton animation="wave" width={80} />
        </TableCell>
        <TableCell>
          <Skeleton animation="wave" width={100} />
        </TableCell>
        <TableCell align="right">
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Skeleton
              animation="wave"
              width={24}
              height={24}
              variant="circular"
            />
            <Skeleton
              animation="wave"
              width={24}
              height={24}
              variant="circular"
            />
            <Skeleton
              animation="wave"
              width={24}
              height={24}
              variant="circular"
            />
            <Skeleton
              animation="wave"
              width={24}
              height={24}
              variant="circular"
            />
          </Stack>
        </TableCell>
      </TableRow>
    ))}
  </>
);

const PageList: React.FC = () => {
  const navigate = useNavigate();
  const [pages, setPages] = useState<Page[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "warning" | "info",
  });

  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "published" | "draft" | "unpublished" | "archived">("all");

  // Pagination state
  const [pagination, setPagination] = useState<PaginationState>({
    page: 0,
    pageSize: 20,
    totalCount: 0,
  });

  useEffect(() => {
    fetchPages();
  }, [pagination.page, pagination.pageSize, searchQuery, statusFilter]);

  const fetchPages = async () => {
    try {
      setLoading(true);
      const params: PageListParams = {
        page: pagination.page + 1, // API uses 1-based pagination
        page_size: pagination.pageSize,
        search: searchQuery,
        ordering: "-created_at",
        is_draft: false,
      };

      // Add status filter if not 'all'
      if (statusFilter !== "all") {
        params.status = statusFilter;
      }

      const response = await pageApi.list(params);
      setPages(response.data.results || response.data);
      setPagination((prev) => ({
        ...prev,
        totalCount: response.data.count || response.data.length,
      }));
      setError(null);
    } catch (error) {
      console.error("Error fetching pages:", error);
      setError("Failed to fetch pages");
      setNotification({
        open: true,
        message: "Failed to fetch pages",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPagination((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPagination((prev) => ({
      ...prev,
      page: 0,
      pageSize: parseInt(event.target.value, 10),
    }));
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setPagination((prev) => ({
      ...prev,
      page: 0,
    }));
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("Are you sure you want to delete this page?")) {
      try {
        await pageApi.delete(id);
        fetchPages();
        setNotification({
          open: true,
          message: "Page deleted successfully",
          severity: "success",
        });
      } catch (err) {
        console.error("Error deleting page:", err);
        setNotification({
          open: true,
          message: "Failed to delete page",
          severity: "error",
        });
      }
    }
  };

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4">Pages</Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => navigate('/cms/pages/hierarchy')}
          >
            Edit Page Hierarchy
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate("/cms/pages/new")}
          >
            New Page
          </Button>
        </Stack>
      </Stack>

      {/* Filters Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              label="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                ),
                endAdornment: searchQuery && (
                  <IconButton size="small" onClick={() => setSearchQuery("")}>
                    <ClearIcon />
                  </IconButton>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) =>
                  setStatusFilter(
                    e.target.value as "all" | "published" | "draft" | "unpublished" | "archived"
                  )
                }
              >
                <SelectMenuItem value="all">All Status</SelectMenuItem>
                <SelectMenuItem value="published">Published</SelectMenuItem>
                <SelectMenuItem value="draft">Draft</SelectMenuItem>
                <SelectMenuItem value="unpublished">Unpublished</SelectMenuItem>
                <SelectMenuItem value="archived">Archived</SelectMenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleClearFilters}
              startIcon={<ClearIcon />}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Full Path</TableCell>
              <TableCell>Template</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <LoadingTableRow />
            ) : pages.length > 0 ? (
              pages.map((page) => (
                <TableRow key={page.id}>
                  <TableCell>{page.title}</TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {page.full_path || page.slug}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={typeof page.template === 'object' ? page.template?.name : 'No Template'}
                      color={page.template ? "primary" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Chip
                        label={page.status.charAt(0).toUpperCase() + page.status.slice(1)}
                        color={
                          page.status === "published" ? "success" : 
                          page.status === "draft" ? "default" :
                          page.status === "unpublished" ? "warning" :
                          "error"
                        }
                        size="small"
                      />
                      {page.has_draft && (
                        <Chip label="Has Draft" color="warning" size="small" />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {new Date(page.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="flex-end"
                    >
                      <Tooltip title="Preview">
                        <IconButton
                          size="small"
                          onClick={() => {
                            window.open(`${import.meta.env.VITE_APP_URL}/${page.full_path}`, '_blank');
                          }}
                        >
                          <OpenInNewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View">
                        <IconButton
                          size="small"
                          onClick={() =>
                            navigate(`/cms/pages/${page.id}/view`)
                          }
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() =>
                            navigate(`/cms/pages/${page.id}/edit`)
                          }
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(page.id)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary" py={2}>
                    No pages found matching your filters
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={pagination.totalCount}
          page={pagination.page}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.pageSize}
          onRowsPerPageChange={handlePageSizeChange}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </TableContainer>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PageList; 